import { Fragment, useCallback, useMemo, useRef } from 'react';

import { StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import DropdownV3 from '@zola/zola-ui/src/components/Form/dropdownV3/DropdownV3';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import TotalItemPagination from '@zola/zola-ui/src/components/Pagination/TotalItemPagination';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';
import { BodyBase, BodySmall } from '@zola/zola-ui/src/typography/Paragraphs';

import styled from '@emotion/styled';
import Sticky from 'react-stickynode';

import { PreAuthVendorCard } from '~/components/common/cards/PreAuthVendorCard/PreAuthVendorCard';
import { cardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import { isSearchLocation } from '~/util/searchUtils';
import TextUtils from '~/util/textUtils';

import { isQuizCardView } from '../../../VendorCardGrid/utils';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorGridCards } from '../../contexts/VendorGridCardsContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import {
  createManualSortingFilter,
  parseSortingValues,
} from '../ManualSortingOptions/ManualSortingOptions';
import { SearchFacetAccordion } from '../SearchControls/facets/FacetSideDrawer/FacetSideDrawer';
import { useSearchCategories } from '../SearchControls/useSearchCategories';
import { AppliedFilters } from './AppliedFilters';
import { getCategoryHeading } from './PageHeading';

const Grid = styled.section<{ noCategory?: boolean }>`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 40px;
  padding: 0 40px;

  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  ${({ noCategory }) => noCategory && `grid-template-columns: 1fr;`};
`;

const DesktopFilters = styled.div`
  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    display: none;
  }
  background-color: ${COLORS3.WHITE_100};
`;

const VendorList = styled.div`
  display: flex;
  flex-direction: column;
`;

const GridHeader = styled.div`
  display: none;

  ${MEDIA_QUERY_V2.DESKTOP} {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 0;
  }
`;

const GridRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SubHeading = () => {
  const {
    vendorSearch,
    searchResults: { totalHits },
  } = useSearchResultsPageContext();

  const { selectedTaxonomy, selectedLocation } = vendorSearch;

  const formattedCount = useMemo(() => {
    const maxShown = selectedTaxonomy || selectedLocation ? 1000 : 10000;

    const numShown = Math.min(maxShown, totalHits);
    let count = TextUtils.formatWithCommas(numShown);

    if (numShown === maxShown) {
      count = `${maxShown}+`;
    }
    return count;
  }, [selectedLocation, selectedTaxonomy, totalHits]);

  const formattedTitle = useMemo(() => {
    const description = `Wedding ${getCategoryHeading(selectedTaxonomy?.key)}`;
    const location = isSearchLocation(selectedLocation) ? selectedLocation.label : undefined;
    let title = description.trim();
    if (location) {
      title = `${title} in ${location}`;
    }
    return title;
  }, [selectedLocation, selectedTaxonomy]);

  return (
    <BodySmall>
      {formattedCount} {formattedTitle}
    </BodySmall>
  );
};

const SortControl = () => {
  const { vendorSearch } = useSearchResultsPageContext();
  const { navigateToSrp } = useVendorSearchNavigation();

  const sortingFilter = createManualSortingFilter(vendorSearch);

  const options = useMemo(
    () =>
      sortingFilter.facets?.map((facet) => {
        return {
          label: facet.displayName,
          value: facet.slug,
        };
      }),
    [sortingFilter.facets]
  );

  const onDropdownSelect = useCallback(
    (value) => {
      const selectedFacet = sortingFilter.facets?.find((facet) => facet.slug === value);

      if (selectedFacet) {
        const [sortBy, sortOrder] = parseSortingValues(selectedFacet.slug);
        if (sortBy && sortOrder) {
          navigateToSrp({ sorting: { sortBy, sortOrder } });
          return;
        }
      }
      navigateToSrp({ sorting: null });
    },
    [navigateToSrp, sortingFilter.facets]
  );

  return (
    <DropdownV3
      // @ts-expect-error noBorder is not declared on chipTrigger props, but all chipTrigger props are passed
      chipTrigger={{ withCaret: true, label: sortingFilter.displayName, noBorder: true }}
      options={options || []}
      value={sortingFilter.slug}
      inline
      mobileOverlay
      boxWidth="max-content"
      boxAlign="right"
      onSelect={onDropdownSelect}
    />
  );
};

const StyledPagination = styled(TotalItemPagination)`
  padding: 40px 0;
`;

const Pagination = () => {
  const { navigateToSrp } = useVendorSearchNavigation();
  const {
    vendorSearch: { page },
    searchResults: { totalHits },
    canonicalUrl,
  } = useSearchResultsPageContext();

  if (totalHits > 0) {
    return (
      <StyledPagination
        currentPage={page}
        currentUrl={canonicalUrl}
        maxPageCountToShow={104}
        onClick={(event, newPage) => {
          // Override the href behavior to scroll to top and navigate to new page client-side
          event.preventDefault();
          window.scrollTo(0, 0);
          navigateToSrp({ page: newPage });
        }}
        pageSize={96}
        totalItems={totalHits}
      />
    );
  }
  return <Fragment />;
};

const GridHeading = () => {
  const {
    searchResults: { totalHits },
  } = useSearchResultsPageContext();

  if (totalHits > 0) {
    return (
      <GridRow>
        <SubHeading />
        <SortControl />
      </GridRow>
    );
  }
  return <Fragment />;
};

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NoResults = () => {
  const {
    searchResults: { totalHits },
  } = useSearchResultsPageContext();

  const { navigateToSrp } = useVendorSearchNavigation();

  const clearFilters = useCallback(() => {
    navigateToSrp({
      awardGroups: null,
      selectedFacets: null,
      price: null,
      capacity: null,
      searchTerm: null,
      availabilityDates: null,
      metroTypes: null,
      catererTypes: null,
      page: undefined,
    });
  }, [navigateToSrp]);

  if (totalHits === 0) {
    return (
      <NoResultsContainer data-testid="no-search-results">
        <Title2 presentation="h4">No results found</Title2>
        <BodyBase>Try adjusting your search or clearing any filters you&apos;ve applied.</BodyBase>
        <div>
          <LinkV2 role="button" onClick={clearFilters} noTextTransform>
            Clear filters
          </LinkV2>
        </div>
      </NoResultsContainer>
    );
  }

  return <Fragment />;
};
export const PreAuthSearchResults = (): JSX.Element => {
  const { vendorSearch, loading } = useSearchResultsPageContext();
  const { selectedTaxonomy } = vendorSearch;
  const { vendorOrQuizCardViews: searchResults } = useVendorGridCards();

  const allFacetCategories = useSearchCategories(vendorSearch);

  const onlyVendorCards = searchResults.filter(
    (card) => !isQuizCardView(card)
  ) as StorefrontCardView[];

  // @see https://github.com/yahoo/react-stickynode/issues/102
  const sidebarDomRef = useRef(null); // needed for resize observer
  const stickyRef = useRef<any>(null); // needed to access sticky instance
  const sidebarResizeObserverRef = useRef<any>({}); // needed to remove observer when component is unloaded

  //  update sticky instance when height changes
  // @see https://github.com/yahoo/react-stickynode/issues/102
  const updateSticky = useCallback(() => {
    const stickyInstance = stickyRef.current;

    if (stickyInstance === null) {
      return;
    }

    stickyInstance.updateInitialDimension();
    stickyInstance.update();
  }, []);

  // observe sidebar height changes
  // @see https://github.com/yahoo/react-stickynode/issues/102
  useEffectOnce(() => {
    const sidebarDomElement = sidebarDomRef.current;

    sidebarResizeObserverRef.current = new ResizeObserver((entries) => {
      // ignore other sidebar observers (not strictly neccessary)
      const sidebarObserverResults = entries.find(({ target }) => target === sidebarDomElement);

      if (typeof sidebarObserverResults === 'undefined') {
        return;
      }

      updateSticky();
    });

    if (sidebarDomElement === null) {
      return;
    }

    sidebarResizeObserverRef.current.observe(sidebarDomElement);

    // cleanup when components is unloaded
    return () => {
      sidebarResizeObserverRef.current.disconnect();
    };
  });

  const hasCategory = Boolean(selectedTaxonomy);

  return (
    <Grid id="grid" noCategory={!hasCategory}>
      {hasCategory && (
        <DesktopFilters>
          <Sticky innerZ={20} top={133} bottomBoundary="#grid" ref={stickyRef}>
            <div ref={sidebarDomRef}>
              <SearchFacetAccordion
                categories={allFacetCategories}
                noBorder
                noSort
                noPadding
                hasSeparateSubmitControl={false}
                collapseOnPriceChange
              />
            </div>
          </Sticky>
        </DesktopFilters>
      )}

      <VendorList>
        <GridHeader>
          <GridHeading />
          <AppliedFilters />
        </GridHeader>

        <NoResults />

        {onlyVendorCards.map((searchResultCardView, index) => {
          return (
            <PreAuthVendorCard
              position={index + 1}
              key={searchResultCardView.storefrontUuid}
              vendor={cardViewToVendorCardVendor(searchResultCardView)}
              loading={loading}
            />
          );
        })}

        <Pagination />
      </VendorList>
    </Grid>
  );
};
